import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { CallLock, Choice, Fan, GameEvent, ReactionEmoji, Rewind, RoomEvent, RoomSnapshot, Take } from '@clutch/core';
import { useClient, store } from '../client';
import type { StreamStatus } from '../api';
import { applyRoomEvent } from './store';
import { rejectionCopy } from './derive';
import { STAGE_TYPES, mergePlays } from '../stage/court';
import type { AnchoredReaction } from '../stage/social';
import { rewindSeenKey, shouldRewind } from '../stage/rewind';

export type FloatingReaction = { id: number; emoji: ReactionEmoji; name: string; x: number; mine: boolean };

const reducer = (s: RoomSnapshot | undefined, e: RoomEvent) => applyRoomEvent(s, e);

/** Read-only room subscription (control room). */
export function useRoomStream(roomId: string | undefined, onEvent?: (e: RoomEvent) => void, fanId?: string) {
  const client = useClient();
  const [snapshot, dispatch] = useReducer(reducer, undefined);
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const cb = useRef(onEvent);
  cb.current = onEvent;
  useEffect(() => {
    if (!roomId) return;
    return client.subscribeRoom(
      roomId,
      (e) => {
        dispatch(e);
        cb.current?.(e);
      },
      setStatus,
      fanId,
    );
  }, [client, roomId, fanId]);
  return { snapshot, status };
}

function idemKey(callId: string, fanId: string): string {
  const k = `clutch.idem.${callId}.${fanId}`;
  const existing = store.get(k);
  if (existing) return existing;
  const fresh =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  store.set(k, fresh);
  return fresh;
}

export type LockUi =
  | { phase: 'idle' }
  | { phase: 'pending'; choice: Choice }
  | { phase: 'locked'; lock: CallLock }
  | { phase: 'rejected'; choice: Choice; message: string };

export type RoomHooks = {
  /** Every applied game event as it arrives (the React cam stamps marks from these). */
  onGameEvent?: (e: GameEvent) => void;
  /** A take published while the fan is in the room (not the initial list). */
  onTake?: (t: Take) => void;
};

/** The fan's room: join, live stream, server-authoritative lock, reactions, court plays, rewind, takes. */
export function useRoom(roomId: string, fan: Fan, hooks: RoomHooks = {}) {
  const client = useClient();
  const hookRef = useRef(hooks);
  hookRef.current = hooks;
  const [takes, setTakes] = useState<Take[]>([]);
  const addTake = useCallback((t: Take) => setTakes((ts) => (ts.some((x) => x.takeId === t.takeId) ? ts : [...ts, t])), []);
  const [reactions, setReactions] = useState<FloatingReaction[]>([]);
  const [anchored, setAnchored] = useState<AnchoredReaction[]>([]);
  const rid = useRef(0);

  const float = useCallback((emoji: ReactionEmoji, name: string, mine: boolean) => {
    const id = ++rid.current;
    const x = 12 + ((id * 37) % 70);
    setReactions((r) => [...r.slice(-11), { id, emoji, name, x, mine }]);
    setTimeout(() => setReactions((r) => r.filter((f) => f.id !== id)), 2400);
  }, []);

  const anchor = useCallback((r: Omit<AnchoredReaction, 'id'>) => {
    const id = ++rid.current;
    setAnchored((a) => [...a.slice(-199), { ...r, id }]);
  }, []);

  // Join first: the server only marks a stream's fan online once they're a member.
  const [joined, setJoined] = useState(false);
  const [joinError, setJoinError] = useState<string>();
  useEffect(() => {
    let alive = true;
    setJoined(false);
    client
      .joinRoom(roomId, fan.fanId)
      .catch(() => alive && setJoinError('Could not join this room — watching only.'))
      .finally(() => alive && setJoined(true));
    return () => {
      alive = false;
    };
  }, [client, roomId, fan.fanId]);

  // Court plays: live game_events, plus a one-time backfill of what the room already applied.
  const [live, setLive] = useState<GameEvent[]>([]);
  const [backfill, setBackfill] = useState<GameEvent[]>([]);
  const [latestSeq, setLatestSeq] = useState<number>(); // the last play that arrived LIVE (only these animate)

  const { snapshot, status } = useRoomStream(
    joined ? roomId : undefined,
    (e) => {
      if (e.kind === 'reaction') {
        if (e.data.fanId === fan.fanId) return; // my own were echoed locally on tap
        float(e.data.emoji, e.data.displayName, false);
        anchor({
          fanId: e.data.fanId,
          name: e.data.displayName,
          emoji: e.data.emoji,
          atSeq: e.data.atSeq,
          ...(e.data.playEventId ? { playEventId: e.data.playEventId } : {}),
          mine: false,
        });
      } else if (e.kind === 'take') {
        addTake(e.data);
        hookRef.current.onTake?.(e.data);
      } else if (e.kind === 'take_removed') {
        setTakes((ts) => ts.filter((t) => t.takeId !== e.data.takeId));
      } else if (e.kind === 'game_event') {
        hookRef.current.onGameEvent?.(e.data.event);
      }
      if (e.kind === 'game_event' && STAGE_TYPES.has(e.data.event.type)) {
        const ev = e.data.event;
        setLive((l) => (l.some((x) => x.seq === ev.seq) ? l : [...l.slice(-399), ev]));
        setLatestSeq(ev.seq);
      } else if (e.kind === 'snapshot') {
        // Fresh snapshot (connect, reconnect, backward seek): never keep plays past it.
        const asOf = e.data.state.asOfSeq;
        setLive((l) => l.filter((x) => x.seq <= asOf));
        setBackfill((b) => b.filter((x) => x.seq <= asOf));
      }
    },
    fan.fanId,
  );

  const asOfSeq = snapshot?.state.asOfSeq ?? -1;
  const haveSnapshot = !!snapshot;
  useEffect(() => {
    if (!haveSnapshot) return;
    let alive = true;
    client
      .plays(roomId)
      .then((p) => alive && setBackfill(p))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [client, roomId, haveSnapshot]);

  // Takes already published in this room (danmu replay + face bubbles).
  useEffect(() => {
    if (!haveSnapshot) return;
    let alive = true;
    client
      .takes(roomId)
      .then((ts) => alive && ts.forEach(addTake))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [client, roomId, haveSnapshot, addTake]);

  const plays = useMemo(() => mergePlays(backfill, live, asOfSeq), [backfill, live, asOfSeq]);
  const backfilled = backfill.length > 0;

  // "What you missed": asked once, when a fan opens a room after tip-off.
  const [rewind, setRewind] = useState<Rewind>();
  const rewindAsked = useRef(false);
  const state = snapshot?.state;
  useEffect(() => {
    if (!state || rewindAsked.current) return;
    rewindAsked.current = true;
    if (!shouldRewind(state, store.get(rewindSeenKey(roomId, fan.fanId), 'session') === '1')) return;
    client
      .rewind(roomId, fan.fanId)
      .then((r) => r && setRewind(r))
      .catch(() => undefined);
  }, [client, roomId, fan.fanId, state]);
  const finishRewind = useCallback(() => {
    store.set(rewindSeenKey(roomId, fan.fanId), '1', 'session');
    setRewind(undefined);
  }, [roomId, fan.fanId]);

  const [local, setLocal] = useState<LockUi>({ phase: 'idle' });
  const serverLock = snapshot?.locks.find((l) => l.fanId === fan.fanId && l.callId === snapshot.call?.callId);
  // Locked is shown ONLY from a server-confirmed lock (stream or POST response).
  const lockUi: LockUi = serverLock ? { phase: 'locked', lock: serverLock } : local;

  const inFlight = useRef(false);
  const lock = useCallback(
    async (choice: Choice) => {
      const call = snapshot?.call;
      if (!call || inFlight.current || serverLock) return;
      inFlight.current = true;
      setLocal({ phase: 'pending', choice });
      try {
        navigator.vibrate?.(12);
      } catch {
        /* unsupported */
      }
      try {
        const res = await client.lock(roomId, call.callId, {
          fanId: fan.fanId,
          choice,
          idempotencyKey: idemKey(call.callId, fan.fanId),
        });
        if (res.status === 'confirmed') setLocal({ phase: 'locked', lock: res });
        else setLocal({ phase: 'rejected', choice, message: rejectionCopy(res.reason, call) });
      } catch {
        setLocal({ phase: 'rejected', choice, message: 'Couldn’t reach Clutch. Tap again — it won’t double-lock.' });
      } finally {
        inFlight.current = false;
      }
    },
    [client, roomId, fan.fanId, snapshot?.call, serverLock],
  );

  const lastPlayId = snapshot?.state.lastPlays[0]?.eventId;
  const react = useCallback(
    (emoji: ReactionEmoji) => {
      float(emoji, fan.displayName, true);
      anchor({
        fanId: fan.fanId,
        name: fan.displayName,
        emoji,
        atSeq: asOfSeq,
        ...(lastPlayId ? { playEventId: lastPlayId } : {}),
        mine: true,
      });
      client.react(roomId, fan.fanId, emoji).catch(() => undefined);
    },
    [client, roomId, fan, float, anchor, asOfSeq, lastPlayId],
  );

  const [challenging, setChallenging] = useState(false);
  const challengeRoom = useCallback(async () => {
    if (challenging) return;
    setChallenging(true);
    try {
      await client.openCall(roomId, fan.fanId);
    } catch {
      /* the call_offer event is the source of truth; a failure just leaves the button */
    } finally {
      setChallenging(false);
    }
  }, [client, roomId, fan.fanId, challenging]);

  return {
    snapshot,
    status,
    takes,
    addTake,
    feed: snapshot?.feed,
    challengeRoom,
    challenging,
    lockUi,
    lock,
    react,
    reactions,
    anchored,
    plays,
    backfilled,
    latestSeq,
    rewind,
    finishRewind,
    joinError,
  };
}
